import {
  Button,
  LoadingIndicator,
  Tooltip,
  TooltipTrigger,
} from "@light-technology/components";
import { ArrowLeft, ArrowRight, HelpCircle, SearchLg } from "@untitledui/icons";
import { useState } from "react";
import plans from "../assets/plans.svg";
import { api } from "../state/api";
import {
  type ActiveAccountCount,
  type AppResponse,
  type PlanResponse,
  type Program,
} from "../state/api.types";
import { useDocumentTitle } from "../utilities/useDocumentTitle";
import { AddPlanButton } from "./AddPlanButton";
import { PlanCard } from "./PlanCard";
import { PlanSlideout } from "./PlanSlideout/PlanSlideout";
import { ProgramCard } from "./ProgramCard";
import { ProgramSlideout } from "./ProgramSlideout/ProgramSlideout";

const PAGE_SIZE = 8;

function buildCountMap(entries: ActiveAccountCount[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const entry of entries) {
    map.set(entry.uuid, entry.active_accounts);
  }
  return map;
}

type PlansPageProps = {
  app: AppResponse;
};

type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

const Pagination = ({ page, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center gap-lg pt-4xl">
      <Button
        color="secondary"
        size="sm"
        iconLeading={ArrowLeft}
        isDisabled={page === 0}
        onClick={() => {
          onPageChange(page - 1);
        }}
      />
      <span className="text-sm text-secondary">
        Page {page + 1} of {totalPages}
      </span>
      <Button
        color="secondary"
        size="sm"
        iconTrailing={ArrowRight}
        isDisabled={page >= totalPages - 1}
        onClick={() => {
          onPageChange(page + 1);
        }}
      />
    </div>
  );
};

type PlanCardsProps = {
  isLoading: boolean;
  plans: PlanResponse[];
  app: AppResponse;
  accountCounts: Map<string, number>;
};

const PlanCards = ({
  isLoading,
  plans,
  app,
  accountCounts,
}: PlanCardsProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-lg p-5xl">
        <LoadingIndicator type="brand-spinner" />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-secondary py-16">
        <div className="flex flex-col items-center gap-6 text-center">
          <SearchLg className="max-w-6 text-featured-icon-light-fg-gray" />
          <div className="flex flex-col gap-1">
            <div className="font-semibold">No plans found</div>
            <div className="text-sm text-tertiary">
              Reach out to us to configure your first plan.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-6">
      {plans.map((plan) => (
        <PlanCard
          key={plan.uuid}
          app={app}
          plan={plan}
          activeAccounts={accountCounts.get(plan.uuid)}
        />
      ))}
    </div>
  );
};

type ProgramCardsProps = {
  isLoading: boolean;
  programs: Program[];
  app: AppResponse;
  accountCounts: Map<string, number>;
};

const ProgramCards = ({
  isLoading,
  programs,
  app,
  accountCounts,
}: ProgramCardsProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-lg p-5xl">
        <LoadingIndicator type="brand-spinner" />
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-secondary py-16">
        <div className="flex flex-col items-center gap-6 text-center">
          <SearchLg className="max-w-6 text-featured-icon-light-fg-gray" />
          <div className="flex flex-col gap-1">
            <div className="font-semibold">No device programs found</div>
            <div className="text-sm text-tertiary">
              Reach out to us to configure your first device program.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-6">
      {programs.map((program) => (
        <ProgramCard
          key={program.uuid}
          app={app}
          program={program}
          activeAccounts={accountCounts.get(program.uuid)}
        />
      ))}
    </div>
  );
};

export const PlansPage = ({ app }: PlansPageProps) => {
  const [plansPage, setPlansPage] = useState(0);
  const [programsPage, setProgramsPage] = useState(0);
  const getAppPlans = api.useGetAppPlansQuery(app.uuid);
  const getAppPrograms = api.useGetAppProgramsQuery(app.uuid);
  const getActiveAccounts = api.useGetCurrentActiveAccountsQuery(app.uuid);

  useDocumentTitle("Offerings");

  const allPlans = getAppPlans.data?.data ?? [];
  const allPrograms = getAppPrograms.data?.data ?? [];

  const totalPlanPages = Math.max(1, Math.ceil(allPlans.length / PAGE_SIZE));
  const totalProgramPages = Math.max(
    1,
    Math.ceil(allPrograms.length / PAGE_SIZE),
  );

  const plansData = allPlans.slice(
    plansPage * PAGE_SIZE,
    (plansPage + 1) * PAGE_SIZE,
  );
  const programsData = allPrograms.slice(
    programsPage * PAGE_SIZE,
    (programsPage + 1) * PAGE_SIZE,
  );

  const planAccountCounts = buildCountMap(getActiveAccounts.data?.plans ?? []);
  const programAccountCounts = buildCountMap(
    getActiveAccounts.data?.programs ?? [],
  );

  return (
    <ProgramSlideout app={app}>
      <PlanSlideout app={app}>
        <div className="p-4xl">
          <div className="mb-7xl flex items-center justify-between">
            <div className="flex items-center gap-xl">
              <img
                src={plans}
                alt="offerings"
                className="hidden h-12 w-12 object-contain min-[400px]:block"
              />
              <div className="text-display-sm font-semibold">Offerings</div>
            </div>
            <AddPlanButton />
          </div>

          <div className="mx-auto w-full max-w-4xl">
            <div className="mb-3xl flex items-center gap-2">
              <span className="text-lg font-semibold">Plans</span>
              <Tooltip
                title="Plans are a comprehensive offer for your customers that determine grid rates and which programs are linked."
                placement="top"
                arrow
              >
                <TooltipTrigger>
                  <HelpCircle className="size-4 text-tertiary" />
                </TooltipTrigger>
              </Tooltip>
            </div>
            <PlanCards
              isLoading={getAppPlans.isLoading}
              plans={plansData}
              app={app}
              accountCounts={planAccountCounts}
            />
            {!getAppPlans.isLoading && (
              <Pagination
                page={plansPage}
                totalPages={totalPlanPages}
                onPageChange={setPlansPage}
              />
            )}

            <div className="mt-7xl mb-3xl flex items-center gap-2">
              <span className="text-lg font-semibold">Device Programs</span>
              <Tooltip
                title="Device programs let you offer customers discounts or earn additional commission for connected devices."
                placement="top"
                arrow
              >
                <TooltipTrigger>
                  <HelpCircle className="size-4 text-tertiary" />
                </TooltipTrigger>
              </Tooltip>
            </div>
            <ProgramCards
              isLoading={getAppPrograms.isLoading}
              programs={programsData}
              app={app}
              accountCounts={programAccountCounts}
            />
            {!getAppPrograms.isLoading && (
              <Pagination
                page={programsPage}
                totalPages={totalProgramPages}
                onPageChange={setProgramsPage}
              />
            )}
          </div>
        </div>
      </PlanSlideout>
    </ProgramSlideout>
  );
};
